import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// AIグラフィック要素コンポーネント
const AiGraphicsOverlay: React.FC = () => {
  // ニューラルネットワークの線を生成（useMemoでパフォーマンス最適化）
  const networkLines = useMemo(() => {
    return Array.from({ length: 30 }).map((_, i) => {
      const x1 = Math.random() * 100;
      const y1 = Math.random() * 100;
      const x2 = Math.random() * 100;
      const y2 = Math.random() * 100;
      return { x1, y1, x2, y2, id: i };
    });
  }, []);

  // 微粒子・ドット要素を生成
  const particles = useMemo(() => {
    return Array.from({ length: 80 }).map((_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 5,
    }));
  }, []);

  return (
    <>
      {/* ニューラルネットワークの線画（SVG） */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(6,182,212,0.3)" stopOpacity="1" />
              <stop offset="100%" stopColor="rgba(124,58,237,0.1)" stopOpacity="1" />
            </linearGradient>
          </defs>
          {networkLines.map((line) => (
            <line
              key={line.id}
              x1={`${line.x1}%`}
              y1={`${line.y1}%`}
              x2={`${line.x2}%`}
              y2={`${line.y2}%`}
              stroke="url(#gradient)"
              strokeWidth={0.5}
              strokeLinecap="round"
              opacity={0.3}
            />
          ))}
        </svg>
      </div>

      {/* 微粒子・ドット要素（CSS） */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <span
            key={particle.id}
            className="absolute w-0.5 h-0.5 rounded-full particle-animate"
            style={{
              top: `${particle.top}%`,
              left: `${particle.left}%`,
              animation: `particleMove ${8 + particle.delay}s linear infinite`,
              opacity: 0.4,
              backgroundColor: particle.id % 2 === 0 ? '#06b6d4' : '#7c3aed',
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes particleMove {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0.4;
          }
          50% {
            opacity: 0.6;
          }
          100% {
            transform: translateY(100vh) translateX(calc(sin(45deg) * 100vw));
            opacity: 0;
          }
        }

        .particle-animate {
          will-change: transform;
        }
      `}</style>
    </>
  );
};

// 画像アセット（Unsplash固定URL - 高品質オフィス・テクノロジーイメージ）
const heroImage = "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1920&h=1080&fit=crop&q=85"; // デジタル・テクノロジー
const premiumImage = "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&h=1080&fit=crop&q=85"; // ビジネス・会議・パートナーシップ
const stepsImage = "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&h=1080&fit=crop&q=85"; // 戦略会議・計画
const supportImage = "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1920&h=1080&fit=crop&q=85"; // テクノロジー・AI環境
const ctaImage = "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&h=1080&fit=crop&q=85"; // プロフェッショナル・相談

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const AISupportPage: React.FC = () => {
  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>{t('ai_support.page_title')}</title>
        <meta name="description" content={t('ai_support.meta_description')} />
        <meta name="keywords" content={t('ai_support.meta_keywords')} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=BIZ+UDGothic:wght@400;700&family=Noto+Sans+JP:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="font-sans" style={{ backgroundColor: '#0a0e1a', color: 'white' }}>

        {/* ナビゲーション */}
        <nav className="container mx-auto px-4 py-6" style={{ backgroundColor: 'rgba(10,14,26,0.95)' }}>
          <Link to="/" className="inline-flex items-center px-4 py-2 rounded-full transition-all duration-300"
            style={{
              backgroundColor: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.8)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="font-bold">{t('ai_support.back_home')}</span>
          </Link>
        </nav>

        {/* Hero セクション */}
        <section
          className="relative py-32 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,14,26,0.92) 0%, rgba(10,14,26,0.85) 50%, rgba(124,58,237,0.3) 100%)' }}></div>
          <AiGraphicsOverlay />
          <div className="container mx-auto px-4 relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              <h1 className="font-black text-6xl md:text-8xl mb-4 leading-tight"
                style={{
                  background: 'linear-gradient(135deg, #06b6d4, #7c3aed)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('ai_support.hero_title')}
              </h1>
              <p className="text-lg md:text-2xl leading-relaxed mb-8 font-semibold max-w-3xl mx-auto" style={{ color: 'rgba(255,255,255,0.85)', textWrap: 'balance', wordBreak: 'keep-all' }}>
                {t('ai_support.hero_subtitle')}
              </p>
              <div className="flex justify-center gap-3 flex-wrap mb-8">
                <span className="px-6 py-2 rounded-full font-bold" style={{ backgroundColor: 'rgba(6,182,212,0.15)', border: '1px solid rgba(6,182,212,0.4)', color: '#06b6d4' }}>{t('ai_support.tag_track_record')}</span>
                <span className="px-6 py-2 rounded-full font-bold" style={{ backgroundColor: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.4)', color: '#a78bfa' }}>{t('ai_support.tag_support')}</span>
                <span className="px-6 py-2 rounded-full font-bold" style={{ backgroundColor: 'rgba(6,182,212,0.15)', border: '1px solid rgba(6,182,212,0.4)', color: '#06b6d4' }}>{t('ai_support.tag_results')}</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 中段3セクション */}
        <section className="relative" style={{ backgroundColor: '#0a0e1a' }}>
          {/* 背景装飾 */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(6,182,212,0.05)' }}></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(124,58,237,0.05)' }}></div>
          </div>

          <div className="relative z-10 py-24">
            {/* プレミアムプラン詳細 */}
            <div className="mx-auto max-w-full px-8 py-12 mb-8">
              <div className="rounded-2xl shadow-2xl p-8"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderTop: '4px solid #06b6d4',
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  className="text-center mb-12"
                >
                  <h2 className="font-black text-4xl md:text-6xl mb-4"
                    style={{
                      background: 'linear-gradient(135deg, #06b6d4, #7c3aed)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {t('ai_support.premium_title')}
                  </h2>
                  <p className="text-lg max-w-2xl mx-auto font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {t('ai_support.premium_subtitle')}
                  </p>
                </motion.div>
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-3 gap-12"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {/* サービス概要 */}
                  <motion.div
                    variants={cardVariants}
                    whileHover={{ scale: 1.02, borderColor: 'rgba(6,182,212,0.4)' }}
                    transition={{ duration: 0.2 }}
                    className="shadow-xl p-8 rounded-xl"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <h3 className="font-black text-2xl mb-4" style={{ color: '#06b6d4' }}>{t('ai_support.overview_title')}</h3>
                    <p className="text-xl leading-loose font-semibold" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      {t('ai_support.overview_body')}
                    </p>
                  </motion.div>
                  {/* 実現できること */}
                  <motion.div
                    variants={cardVariants}
                    whileHover={{ scale: 1.02, borderColor: 'rgba(6,182,212,0.4)' }}
                    transition={{ duration: 0.2 }}
                    className="shadow-xl p-8 rounded-xl"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <h3 className="font-black text-2xl mb-4" style={{ color: '#06b6d4' }}>{t('ai_support.achieve_title')}</h3>
                    <ul className="leading-relaxed space-y-3 font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      <li>✅ {t('ai_support.achieve_item1_main')}<br /><span className="text-sm font-normal" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('ai_support.achieve_item1_sub')}</span></li>
                      <li>✅ {t('ai_support.achieve_item2_main')}<br /><span className="text-sm font-normal" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('ai_support.achieve_item2_sub')}</span></li>
                      <li>✅ {t('ai_support.achieve_item3_main')}<br /><span className="text-sm font-normal" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('ai_support.achieve_item3_sub')}</span></li>
                      <li>✅ {t('ai_support.achieve_item4_main')}<br /><span className="text-sm font-normal" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('ai_support.achieve_item4_sub')}</span></li>
                      <li>✅ {t('ai_support.achieve_item5_main')}<br /><span className="text-sm font-normal" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('ai_support.achieve_item5_sub')}</span></li>
                    </ul>
                  </motion.div>
                  {/* 対象企業 */}
                  <motion.div
                    variants={cardVariants}
                    whileHover={{ scale: 1.02, borderColor: 'rgba(6,182,212,0.4)' }}
                    transition={{ duration: 0.2 }}
                    className="shadow-xl p-8 rounded-xl"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <h3 className="font-black text-2xl mb-4" style={{ color: '#06b6d4' }}>{t('ai_support.target_title')}</h3>
                    <p className="leading-relaxed font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      {t('ai_support.target_intro')}
                    </p>
                    <ul className="leading-relaxed space-y-2 font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      <li>✅ {t('ai_support.target_item1')}</li>
                      <li>✅ {t('ai_support.target_item2')}</li>
                      <li>✅ {t('ai_support.target_item3')}</li>
                      <li>✅ {t('ai_support.target_item4')}</li>
                      <li>✅ {t('ai_support.target_item5')}</li>
                      <li>✅ {t('ai_support.target_item6')}</li>
                      <li>✅ {t('ai_support.target_item7')}</li>
                      <li>✅ {t('ai_support.target_item8')}</li>
                    </ul>
                  </motion.div>
                </motion.div>
              </div>
            </div>

            {/* 3つのステップ */}
            <div className="mx-auto max-w-full px-8 py-12 mb-8">
              <div className="rounded-2xl shadow-2xl p-8"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderTop: '4px solid #7c3aed',
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  className="text-center mb-12"
                >
                  <h2 className="font-black text-4xl md:text-6xl mb-4"
                    style={{
                      background: 'linear-gradient(135deg, #7c3aed, #06b6d4)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {t('ai_support.steps_title')}
                  </h2>
                  <p className="text-lg max-w-2xl mx-auto font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {t('ai_support.steps_subtitle')}
                  </p>
                </motion.div>
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-3 gap-12"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {/* AI戦略策定 */}
                  <motion.div
                    variants={cardVariants}
                    whileHover={{ scale: 1.02, borderColor: 'rgba(124,58,237,0.4)' }}
                    transition={{ duration: 0.2 }}
                    className="shadow-xl p-8 rounded-xl"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <h3 className="font-black text-2xl mb-4" style={{ color: '#a78bfa' }}>{t('ai_support.step1_title')}</h3>
                    <p className="leading-relaxed font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      {t('ai_support.step1_body')}
                    </p>
                  </motion.div>
                  {/* AI導入・実装 */}
                  <motion.div
                    variants={cardVariants}
                    whileHover={{ scale: 1.02, borderColor: 'rgba(124,58,237,0.4)' }}
                    transition={{ duration: 0.2 }}
                    className="shadow-xl p-8 rounded-xl"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <h3 className="font-black text-2xl mb-4" style={{ color: '#a78bfa' }}>{t('ai_support.step2_title')}</h3>
                    <p className="leading-relaxed font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      {t('ai_support.step2_body')}
                    </p>
                  </motion.div>
                  {/* 研修・定着支援 */}
                  <motion.div
                    variants={cardVariants}
                    whileHover={{ scale: 1.02, borderColor: 'rgba(124,58,237,0.4)' }}
                    transition={{ duration: 0.2 }}
                    className="shadow-xl p-8 rounded-xl"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <h3 className="font-black text-2xl mb-4" style={{ color: '#a78bfa' }}>{t('ai_support.step3_title')}</h3>
                    <p className="leading-relaxed font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      {t('ai_support.step3_body')}
                    </p>
                  </motion.div>
                </motion.div>
              </div>
            </div>

            {/* 充実したサポート内容 */}
            <div className="mx-auto max-w-full px-8 py-12">
              <div className="rounded-2xl shadow-2xl p-8"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderTop: '4px solid #06b6d4',
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  className="text-center mb-12"
                >
                  <h2 className="font-black text-4xl md:text-6xl mb-4"
                    style={{
                      background: 'linear-gradient(135deg, #06b6d4, #7c3aed)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {t('ai_support.support_title')}
                  </h2>
                </motion.div>
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-3 gap-12"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {/* エージェントAI環境構築 */}
                  <motion.div
                    variants={cardVariants}
                    whileHover={{ scale: 1.02, borderColor: 'rgba(6,182,212,0.4)' }}
                    transition={{ duration: 0.2 }}
                    className="shadow-xl p-8 rounded-xl"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <h3 className="font-black text-2xl mb-4" style={{ color: '#06b6d4' }}>{t('ai_support.support1_title')}</h3>
                    <p className="leading-relaxed font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      {t('ai_support.support1_body')}
                    </p>
                  </motion.div>
                  {/* AI活用コンサルティング */}
                  <motion.div
                    variants={cardVariants}
                    whileHover={{ scale: 1.02, borderColor: 'rgba(6,182,212,0.4)' }}
                    transition={{ duration: 0.2 }}
                    className="shadow-xl p-10 rounded-xl"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <h3 className="font-black text-2xl mb-4" style={{ color: '#06b6d4' }}>{t('ai_support.support2_title')}</h3>
                    <p className="leading-relaxed font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      {t('ai_support.support2_body')}
                    </p>
                  </motion.div>
                  {/* AI人材育成支援 */}
                  <motion.div
                    variants={cardVariants}
                    whileHover={{ scale: 1.02, borderColor: 'rgba(6,182,212,0.4)' }}
                    transition={{ duration: 0.2 }}
                    className="shadow-xl p-8 rounded-xl"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <h3 className="font-black text-2xl mb-4" style={{ color: '#06b6d4' }}>{t('ai_support.support3_title')}</h3>
                    <p className="leading-relaxed font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      {t('ai_support.support3_body')}
                    </p>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA セクション */}
        <section
          className="relative py-32 bg-cover bg-center min-h-screen flex items-center"
          style={{ backgroundImage: `url(${ctaImage})` }}
        >
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,14,26,0.92) 0%, rgba(124,58,237,0.6) 50%, rgba(6,182,212,0.4) 100%)' }}></div>
          <div className="container mx-auto px-4 relative z-10 text-center w-full">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              <h2 className="font-black text-5xl md:text-7xl text-white mb-8 leading-tight">
                {t('ai_support.cta_title')}
              </h2>
              <p className="text-lg md:text-2xl mb-12 max-w-3xl mx-auto font-semibold leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                {t('ai_support.cta_body')}<br />
                <span style={{ color: '#06b6d4' }}>{t('ai_support.cta_free')}</span>{t('ai_support.cta_middle')}
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLSfGctjmssSGu73JcGfPeECrLstNGZF5w_36ePFOZLw7s-1HPg/viewform', '_blank', 'noopener,noreferrer')}
                className="inline-block font-black py-4 px-12 rounded-full shadow-2xl text-xl cursor-pointer border-none transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, #06b6d4, #7c3aed)',
                  color: 'white',
                  boxShadow: '0 0 30px rgba(6,182,212,0.4)',
                }}
              >
                {t('ai_support.cta_button')}
              </motion.button>
              <p className="mt-8 font-semibold" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {t('ai_support.cta_note')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
            © {new Date().getFullYear()} {t('ai_support.footer')}
          </p>
        </footer>

      </div>
    </>
  );
};

export default AISupportPage;
